import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addEdge,
  Background,
  Connection,
  ConnectionLineType,
  Edge,
  EdgeTypes,
  MiniMap,
  Node,
  NodeTypes,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "@/styles/flow-builder.css";
import { Maximize2, MoveHorizontal, MoveVertical, Plus, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ActionNodeData, FlowOrientation } from "@/model/interface/CallFlow";
import { CallFlowActionType } from "@/model/interface/CallFlowActionType";
import { edgeKeysFor, edgeLabelFor, isTerminalNodeType, NODE_LABELS, withActionDefaults } from "@/util/callFlowNodes";
import { layoutFlow } from "@/util/flowLayout";
import ActionNode from "../nodes/ActionNode";
import PickerNode from "../nodes/PickerNode";
import StartNode from "../nodes/StartNode";
import FlowEdge from "./FlowEdge";
import { FlowBuilderActions, FlowBuilderContext } from "./FlowBuilderContext";

const nodeTypes: NodeTypes = {
  start: StartNode,
  action: ActionNode,
  end: ActionNode,
  picker: PickerNode,
};

const edgeTypes: EdgeTypes = {
  flow: FlowEdge,
};

const FLOW_EDGE_TYPE = "flow";
const START_NODE_ID = "start";
const ORIENTATION_STORAGE_KEY = "flowBuilderOrientation";
const FIT_PADDING = 0.08;
const FIT_MAX_ZOOM = 1.35;

type FocusRequest = { nodeIds: string[] } | "all";

interface FlowBuilderProps {
  initialNodes?: Node[];
  initialEdges?: Edge[];
  onSave?: (nodes: Node[], edges: Edge[]) => void;
}

export default function FlowBuilder(props: FlowBuilderProps) {
  return (
    <ReactFlowProvider>
      <FlowBuilderCanvas {...props} />
    </ReactFlowProvider>
  );
}

function FlowBuilderCanvas({ initialNodes = [], initialEdges = [], onSave }: FlowBuilderProps) {
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const [orientation, setOrientation] = useState<FlowOrientation>(readStoredOrientation);
  const [startingNodes] = useState(() => normalizeNodes(initialNodes));
  const [startingEdges] = useState(() => normalizeEdges(initialEdges));
  const [nodes, setNodes, onNodesChange] = useNodesState(startingNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(startingEdges);
  const [hasLaidOut, setHasLaidOut] = useState(false);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const focusRequest = useRef<FocusRequest | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const viewportTouched = useRef(false);
  const latestNodes = useRef(nodes);
  latestNodes.current = nodes;

  const isMeasured = nodes.every((node) => (node.measured?.width ?? 0) > 0 && (node.measured?.height ?? 0) > 0);
  const layoutSignature = [
    orientation,
    nodes.map((node) => `${node.id}:${node.type}:${node.measured?.width ?? 0}x${node.measured?.height ?? 0}`).join("|"),
    edges.map((edge) => `${edge.source}>${edge.target}`).join("|"),
  ].join("#");

  const onConnect = useCallback(
    (connection: Connection) =>
      setEdges((currentEdges) =>
        addEdge(
          {
            ...connection,
            type: FLOW_EDGE_TYPE,
            label: connection.sourceHandle ? edgeLabelFor(connection.sourceHandle) : undefined,
          },
          withoutHandleEdge(currentEdges, connection.source, connection.sourceHandle),
        ),
      ),
    [setEdges],
  );

  const addPickerNode = useCallback(
    (source?: { nodeId: string; handle?: string }) => {
      const nodeId = `node-${Date.now()}`;
      const sourceNode = source ? latestNodes.current.find((node) => node.id === source.nodeId) : undefined;
      const position = sourceNode
        ? orientation === "horizontal"
          ? { x: sourceNode.position.x + (sourceNode.measured?.width ?? 0), y: sourceNode.position.y }
          : { x: sourceNode.position.x, y: sourceNode.position.y + (sourceNode.measured?.height ?? 0) }
        : { x: 0, y: 0 };

      setNodes((currentNodes) => [...currentNodes, { id: nodeId, type: "picker", position, data: {} }]);

      if (source) {
        setEdges((currentEdges) => [
          ...withoutHandleEdge(currentEdges, source.nodeId, source.handle),
          {
            id: `edge-${source.nodeId}-${source.handle ?? "next"}-${nodeId}`,
            source: source.nodeId,
            sourceHandle: source.handle,
            target: nodeId,
            type: FLOW_EDGE_TYPE,
            label: source.handle ? edgeLabelFor(source.handle) : undefined,
          },
        ]);
      }

      focusRequest.current = { nodeIds: source ? [source.nodeId, nodeId] : [nodeId] };
    },
    [orientation, setNodes, setEdges],
  );

  const addLinkedNode = useCallback(
    (sourceId: string, sourceHandle?: string) => {
      addPickerNode({ nodeId: sourceId, handle: sourceHandle });
    },
    [addPickerNode],
  );

  const chooseActionType = useCallback(
    (nodeId: string, actionType: CallFlowActionType) => {
      const node = latestNodes.current.find((candidate) => candidate.id === nodeId);
      if (!node) {
        return;
      }
      const nextData = withActionDefaults({
        ...(node.data as Partial<ActionNodeData>),
        actionType,
        label: NODE_LABELS[actionType],
      });
      const allowedHandles = edgeKeysFor(actionType, nextData);

      setNodes((currentNodes) =>
        currentNodes.map((candidate) =>
          candidate.id === nodeId
            ? { ...candidate, type: isTerminalNodeType(actionType) ? "end" : "action", data: nextData }
            : candidate,
        ),
      );
      setEdges((currentEdges) =>
        currentEdges.filter((edge) => edge.source !== nodeId || allowedHandles.includes(edge.sourceHandle ?? "")),
      );
    },
    [setNodes, setEdges],
  );

  const deleteNode = useCallback(
    (nodeId: string) => {
      setNodes((currentNodes) => currentNodes.filter((node) => node.id !== nodeId));
      setEdges((currentEdges) => currentEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    },
    [setNodes, setEdges],
  );

  const toggleOrientation = () => {
    const nextOrientation: FlowOrientation = orientation === "vertical" ? "horizontal" : "vertical";
    localStorage.setItem(ORIENTATION_STORAGE_KEY, nextOrientation);
    focusRequest.current = "all";
    setOrientation(nextOrientation);
  };

  const builderActions = useMemo<FlowBuilderActions>(
    () => ({ orientation, deleteNode, chooseActionType, addLinkedNode }),
    [orientation, deleteNode, chooseActionType, addLinkedNode],
  );

  useEffect(() => {
    if (!isMeasured) {
      return;
    }
    setNodes((currentNodes) => layoutFlow(currentNodes, edges, orientation));
    setLayoutVersion((version) => version + 1);
  }, [layoutSignature]);

  useEffect(() => {
    if (layoutVersion === 0) {
      return;
    }
    const request = focusRequest.current ?? (hasLaidOut ? null : "all");
    focusRequest.current = null;
    if (!request) {
      return;
    }
    const animationFrame = requestAnimationFrame(() => {
      const nodeIds = request === "all" ? latestNodes.current.map((node) => node.id) : request.nodeIds;
      fitView({
        nodes: nodeIds.map((id) => ({ id })),
        padding: FIT_PADDING,
        maxZoom: FIT_MAX_ZOOM,
        duration: hasLaidOut ? 400 : 0,
      });
      setHasLaidOut(true);
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [layoutVersion]);

  useEffect(() => {
    onSave?.(nodes, edges);
  }, [nodes, edges, onSave]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !hasLaidOut) {
      return;
    }
    let initialObservation = true;
    const observer = new ResizeObserver(() => {
      if (initialObservation) {
        initialObservation = false;
        return;
      }
      if (viewportTouched.current) {
        return;
      }
      fitView({ padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: 200 });
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [hasLaidOut, fitView]);

  return (
    <FlowBuilderContext.Provider value={builderActions}>
      <div ref={wrapperRef} className={cn("flow-builder relative h-full w-full", !hasLaidOut && "opacity-0")}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onMoveStart={(event) => {
            if (event) {
              viewportTouched.current = true;
            }
          }}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{ type: FLOW_EDGE_TYPE }}
          connectionLineType={ConnectionLineType.SmoothStep}
          connectionRadius={40}
          nodesDraggable={false}
          zoomOnDoubleClick={false}
          minZoom={0.2}
          maxZoom={1.5}
          className="bg-background"
        >
          <Background gap={24} size={1.5} />
          <MiniMap pannable zoomable />
        </ReactFlow>

        <div className="absolute right-4 top-4 z-10 flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => zoomOut({ duration: 200 })}
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => zoomIn({ duration: 200 })}
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => fitView({ padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: 300 })}
            title="Fit the flow on screen"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleOrientation}
            title={orientation === "vertical" ? "Switch to horizontal layout" : "Switch to vertical layout"}
          >
            {orientation === "vertical" ? <MoveHorizontal className="h-4 w-4" /> : <MoveVertical className="h-4 w-4" />}
          </Button>
          <Button type="button" onClick={() => addPickerNode()}>
            <Plus className="mr-2 h-4 w-4" />
            Add Action
          </Button>
        </div>
      </div>
    </FlowBuilderContext.Provider>
  );
}

function readStoredOrientation(): FlowOrientation {
  return localStorage.getItem(ORIENTATION_STORAGE_KEY) === "horizontal" ? "horizontal" : "vertical";
}

function normalizeNodes(nodes: Node[]): Node[] {
  if (nodes.length === 0) {
    return [{ id: START_NODE_ID, type: "start", position: { x: 0, y: 0 }, data: {}, deletable: false }];
  }
  return nodes.map((node) => {
    if (node.type === "start") {
      return { ...node, deletable: false };
    }
    if (node.type !== "action" && node.type !== "end") {
      return node;
    }
    return { ...node, data: withActionDefaults(node.data as ActionNodeData) };
  });
}

function normalizeEdges(edges: Edge[]): Edge[] {
  return edges.map((edge) => ({ ...edge, type: FLOW_EDGE_TYPE }));
}

function withoutHandleEdge(edges: Edge[], source: string, sourceHandle?: string | null): Edge[] {
  return edges.filter((edge) => !(edge.source === source && (edge.sourceHandle ?? null) === (sourceHandle ?? null)));
}
